import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Loader2, Save, Sliders, Wrench, Zap, Droplet, Hammer, Home } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-right tabular-nums focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

const SPECIALTIES = [
  { key: "plumbing",       label: "Plumbing",        icon: Droplet, color: "text-cyan-600 bg-cyan-50" },
  { key: "hvac",           label: "HVAC / Mechanical", icon: Wrench, color: "text-orange-600 bg-orange-50" },
  { key: "electrical",     label: "Electrical",      icon: Zap,    color: "text-yellow-600 bg-yellow-50" },
  { key: "carpentry",      label: "Carpentry / Drywall / Window", icon: Hammer, color: "text-lime-600 bg-lime-50" },
  { key: "general_repair", label: "General Repair / Landscape / Snow", icon: Home, color: "text-slate-600 bg-slate-100" },
];

export default function Settings() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/tm-defaults").then(setCfg).catch(() => toast.error(t("error")));
  }, []);

  if (!cfg) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const setSpec = (key, field, value) => {
    const next = { ...cfg.specialties, [key]: { ...(cfg.specialties[key] || {}), [field]: parseFloat(value) || 0 } };
    setCfg({ ...cfg, specialties: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const out = await api.put("/settings/tm-defaults", cfg);
      setCfg(out);
      toast.success(t("saved"));
    } catch { toast.error(t("error")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Sliders className="w-7 h-7" /> {t("settingsTitle") || "Billing Settings"}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("settingsSubtitle") || "Configure Service Call fees, labor rates and material markup used to auto-calculate T&M invoices."}</p>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("save")}
        </button>
      </div>

      {/* Per-specialty rates */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t("settingsSpecialties") || "Rates by Specialty"}</h2>
          <p className="text-xs text-slate-500 mt-1">{t("settingsSpecialtiesHint") || "Visit fee is charged once per day of on-site work. Hourly rate applies to each technician's clocked hours."}</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50/60">
              <th className="text-left px-5 py-2">{t("specialty") || "Specialty"}</th>
              <th className="text-right px-5 py-2 w-40">{t("settingsVisitFee") || "Visit Fee ($)"}</th>
              <th className="text-right px-5 py-2 w-40">{t("settingsHourlyRate") || "Hourly Rate ($/h)"}</th>
            </tr>
          </thead>
          <tbody>
            {SPECIALTIES.map(({ key, label, icon: Icon, color }) => {
              const s = cfg.specialties?.[key] || {};
              return (
                <tr key={key} className="border-b border-slate-100 last:border-b-0" data-testid={`row-spec-${key}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-7 h-7 rounded-md flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></span>
                      <span className="font-medium text-slate-800">{label}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <input data-testid={`spec-${key}-visit-fee`} type="number" step="0.01" min="0" value={s.visit_fee ?? 0}
                      onChange={(e) => setSpec(key, "visit_fee", e.target.value)} className={inputCls} />
                  </td>
                  <td className="px-5 py-3">
                    <input data-testid={`spec-${key}-hourly-rate`} type="number" step="0.01" min="0" value={s.hourly_rate ?? 0}
                      onChange={(e) => setSpec(key, "hourly_rate", e.target.value)} className={inputCls} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Step templates */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t("stepTemplatesTitle") || "Step Templates"}</h2>
          <p className="text-xs text-slate-500 mt-1">{t("stepTemplatesHint") || "Default workflow applied when a Service Call is created. Comma-separated. The manager can edit these on each service call."}</p>
        </div>
        <div className="p-5 space-y-3">
          {SPECIALTIES.map(({ key, label, icon: Icon, color }) => {
            const steps = (cfg.step_templates?.[key] || []);
            return (
              <div key={key} className="flex items-start gap-3" data-testid={`tpl-${key}`}>
                <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${color}`}><Icon className="w-4 h-4" /></span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-800">{label}</div>
                  <input
                    data-testid={`tpl-${key}-input`}
                    value={steps.join(", ")}
                    onChange={(e) => setCfg({ ...cfg, step_templates: { ...(cfg.step_templates || {}), [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition"
                    placeholder="Visit, Diagnostic, Repair, Test"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Global settings */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">{t("settingsGlobal") || "Global Defaults"}</h2>
          <p className="text-xs text-slate-500 mt-1">{t("settingsGlobalHint") || "Apply to all specialties. Material markup adds a percentage on top of the receipt cost when billing the client."}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 p-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tmMaterialMarkup") || "Material Markup"} (%)</label>
            <input data-testid="global-material-markup" type="number" step="1" min="0" value={cfg.material_markup_pct}
              onChange={(e) => setCfg({ ...cfg, material_markup_pct: parseFloat(e.target.value) || 0 })} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tmMinHours") || "Minimum Billable Hours"}</label>
            <input data-testid="global-min-hours" type="number" step="0.5" min="0" value={cfg.min_hours}
              onChange={(e) => setCfg({ ...cfg, min_hours: parseFloat(e.target.value) || 0 })} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("geofenceRadius") || "Geofence Radius (mi)"}</label>
            <input data-testid="global-geofence-radius" type="number" step="0.25" min="0" value={cfg.geofence_radius_miles ?? 2}
              onChange={(e) => setCfg({ ...cfg, geofence_radius_miles: parseFloat(e.target.value) || 0 })} className={inputCls} />
            <p className="text-[10px] text-slate-400 mt-1">{t("geofenceRadiusHint") || "Alerts tech when leaving the job site by more than this distance."}</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tmLaborMode") || "Labor Mode"}</label>
            <select data-testid="global-labor-mode" value={cfg.labor_mode}
              onChange={(e) => setCfg({ ...cfg, labor_mode: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition">
              <option value="rate">{t("tmModeRate") || "Fixed Hourly Rate"}</option>
              <option value="markup">{t("tmModeMarkup") || "Cost Markup"}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
